import { Observable, of, zip } from 'rxjs';
import { concatMap, map, mergeMap } from 'rxjs/operators';
import { performAddEventProfileTranslations } from '~admin/interactor/event/perform-add-event-profile-translations';
import { performCreateEventProfile } from '~admin/interactor/event/perform-create-event-profile';
import { performAddMarketplaceTranslations } from '~admin/interactor/marketplace/perform-add-marketplace-translations';
import { performCreateMarketplace } from '~admin/interactor/marketplace/perform-create-marketplace';
import { performEnableMarketplace } from '~admin/interactor/marketplace/perform-enable-marketplace';
import { performAddOfferGroupTranslations } from '~admin/interactor/offer-group/perform-add-offer-group-translations';
import { performCreateOfferGroup } from '~admin/interactor/offer-group/perform-create-offer-group';
import {
  getEventProfileRequest,
  getEventTranslationRequest,
} from '~admin/view/components/create-event/helper/event-profile-request.helper';
import {
  getMarketplaceRequest,
  getMarketplaceTranslationsRequest,
} from '~admin/view/components/create-event/helper/marketplace-request.helper';
import {
  getOfferGroupRequest,
  getOfferGroupTranslationsRequest,
} from '~admin/view/components/create-event/helper/offer-group-request.helper';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { MarketplaceFormData } from '~admin/view/components/details/marketplace-details/marketplace-details-fields.component';
import { OfferGroupFormData } from '~admin/view/components/details/offer-group-details/offer-group-fields.component';
import { EventResponse } from '~client/model/admin/event-response';
import { MarketplaceResponse } from '~client/model/admin/marketplace-response';
import { OfferGroupResponse } from '~client/model/admin/offer-group-response';
import { isNilOrEmpty } from '~common/helper/global';
import { OneArgTask, rxTask } from '~common/helper/mobx-tasks';

const createOfferGroups = (
  offerGroups: OfferGroupFormData[],
  createMarketplaceResponse: MarketplaceResponse,
): Observable<OfferGroupResponse | {}> => {
  if (isNilOrEmpty(offerGroups)) {
    return of({});
  } else {
    return of(...offerGroups).pipe(
      mergeMap((offerGroup) =>
        performCreateOfferGroup(
          getOfferGroupRequest(
            createMarketplaceResponse.id,
            null, //  TODO implement this for buy now
            offerGroup,
          ),
        ).pipe(
          mergeMap((offerGroupResponse) =>
            performAddOfferGroupTranslations(
              getOfferGroupTranslationsRequest(
                offerGroupResponse.id,
                offerGroupResponse.suggestedOfferIncrementIds,
                offerGroup,
              ),
            ).pipe(map(() => offerGroupResponse)),
          ),
        ),
      ),
    );
  }
};

const createMarketplaces = (
  marketplaces: MarketplaceFormData[],
  createEventProfileResponse: EventResponse,
): Observable<MarketplaceResponse> =>
  of(...marketplaces).pipe(
    mergeMap((marketplace) =>
      performCreateMarketplace(
        getMarketplaceRequest(createEventProfileResponse.id, marketplace),
      ).pipe(
        mergeMap((createMarketplaceResponse) =>
          zip(
            performAddMarketplaceTranslations(
              getMarketplaceTranslationsRequest(
                createMarketplaceResponse.id.toString(),
                marketplace,
              ),
            ),
            createOfferGroups(marketplace.offerGroups, createMarketplaceResponse),
          ).pipe(concatMap(() => performEnableMarketplace(createMarketplaceResponse.id))),
        ),
      ),
    ),
  );

export const createEventTaskFactory = (): OneArgTask<EventFormData, MarketplaceResponse> =>
  rxTask<EventFormData, any>((eventData: EventFormData) =>
    performCreateEventProfile(getEventProfileRequest(eventData)).pipe(
      mergeMap((createEventProfileResponse: EventResponse) =>
        zip(
          performAddEventProfileTranslations(
            getEventTranslationRequest(
              createEventProfileResponse.id.toString(),
              createEventProfileResponse.eventRestrictions,
              createEventProfileResponse.eventTimelineGroups,
              eventData,
            ),
          ),
          createMarketplaces(eventData.marketplaces, createEventProfileResponse),
        ),
      ),
    ),
  );



// WEBPACK FOOTER //
// ./interactor/event/create-event.ts